import { journeySteps } from '../../../mocks/bookData';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useEffect, useRef, useState } from 'react';

export default function Journey() {
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const timelineReveal = useScrollReveal({ threshold: 0.1 });
  const [lineProgress, setLineProgress] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Animate timeline line drawing
  useEffect(() => {
    if (!timelineReveal.isVisible) return;

    const handleScroll = () => {
      if (!timelineRef.current) return;
      
      const rect = timelineRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementHeight = rect.height;
      
      // Calculate progress based on scroll position
      const scrollProgress = Math.max(0, Math.min(1, (windowHeight - elementTop) / (windowHeight + elementHeight)));
      setLineProgress(scrollProgress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [timelineReveal.isVisible]);

  return (
    <section className="py-24 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-5xl mx-auto px-6">
        <h2 
          ref={headerReveal.ref}
          className={`font-serif text-5xl font-bold text-gray-900 text-center mb-20 ${headerReveal.isVisible ? 'opacity-100 animate-fade-in-up' : 'opacity-0'}`}
        >
          JŪSŲ 6 ŽINGSNIŲ KELIONĖ
        </h2>

        <div className="relative" ref={timelineRef}>
          {/* Center line with draw animation */}
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 hidden lg:block">
            <div 
              className="absolute top-0 left-0 w-full bg-gradient-to-b from-purple-500 via-pink-500 to-purple-500 transition-all duration-300 ease-out"
              style={{ height: `${lineProgress * 100}%` }}
            ></div>
          </div>

          {/* Steps */}
          <div className="space-y-12" ref={timelineReveal.ref}>
            {journeySteps.map((step, index) => (
              <div 
                key={step.number}
                className={`relative flex items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content card with alternating slide-in */}
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div className={`bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 ${
                    timelineReveal.isVisible 
                      ? `opacity-100 ${index % 2 === 0 ? 'animate-fade-in-right' : 'animate-fade-in-left'} stagger-${index + 1}` 
                      : 'opacity-0'
                  }`}>
                    <div className={`flex items-center gap-4 mb-3 ${index % 2 === 0 ? 'lg:justify-end' : 'lg:justify-start'}`}>
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        {step.number}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Center dot with pulse animation */}
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2">
                  <div 
                    className={`w-6 h-6 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full border-4 border-white shadow-lg ${
                      timelineReveal.isVisible && lineProgress > (index / journeySteps.length)
                        ? 'opacity-100 animate-scale-in'
                        : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    {/* Pulse ring */}
                    <div className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-75"></div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="flex-1 hidden lg:block"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Highlighted text after journey */}
        <div className="mt-16 text-center">
          <p className={`text-2xl font-bold text-gray-900 bg-gradient-to-r from-purple-100 via-pink-100 to-purple-100 py-6 px-8 rounded-2xl shadow-md inline-block ${
            timelineReveal.isVisible ? 'opacity-100 animate-scale-in stagger-8' : 'opacity-0'
          }`}>
            Tai knyga, kurią ne tik skaitai – ją realiai naudoji.
          </p>
        </div>
      </div>
    </section>
  );
}